import { type SchemaOrg, type SchemaTypes, type PostalAddress, type GeoCoordinates, type OpeningHoursSpecification } from '../types/schema.ts';

interface BaseSchema extends SchemaOrg {
  '@context': 'https://schema.org';
  '@type': string | string[];
  '@id': string;
  name: string;
  url: string;
  telephone: string;
  email: string;
  description: string;
  address: PostalAddress;
  geo: GeoCoordinates;
  openingHoursSpecification: OpeningHoursSpecification[];
  sameAs: string[];
  areaServed: string[];
  priceRange: string;
}

interface ServiceSchema extends SchemaOrg {
  '@context': 'https://schema.org';
  '@type': string;
  name: string;
  description: string;
  provider: { '@id': string };
  availableService: Array<{
    '@context': 'https://schema.org';
    '@type': string;
    name: string;
    description: string;
  }>;
}

const baseSchema: BaseSchema = {
  '@context': 'https://schema.org',
  '@type': ['MedicalBusiness', 'MedicalOrganization', 'LocalBusiness'],
  '@id': 'https://metrocounselling.ca/#organization',
  name: 'Metro Counselling & Therapy Services',
  url: 'https://metrocounselling.ca',
  telephone: '+1-604-619-9992',
  email: 'info@metrocounselling.ca',
  description: 'Professional counselling and therapy services in Surrey, BC. Specializing in individual, couples, family, anxiety, depression, trauma, and ICBC counselling.',
  address: {
    '@context': 'https://schema.org',
    '@type': 'PostalAddress',
    streetAddress: 'Unit 201, 10366 136A Street',
    addressLocality: 'Surrey',
    addressRegion: 'BC',
    postalCode: 'V3T 1W4',
    addressCountry: 'CA'
  },
  geo: {
    '@context': 'https://schema.org',
    '@type': 'GeoCoordinates',
    latitude: '49.190932',
    longitude: '-122.848628'
  },
  openingHoursSpecification: [
    {
      '@context': 'https://schema.org',
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '10:00',
      closes: '16:00'
    }
  ],
  sameAs: [
    'https://www.facebook.com/metrocounsellingtherapy/',
    'https://ca.linkedin.com/company/metrocounselling'
  ],
  areaServed: ['Surrey', 'Vancouver', 'Burnaby', 'Delta', 'Langley', 'White Rock', 'British Columbia'],
  priceRange: '$$'
};

const serviceSchema: ServiceSchema = {
  '@context': 'https://schema.org',
  '@type': 'MedicalTherapy',
  name: 'Counselling Services',
  description: 'Professional counselling services including individual therapy, couples counselling, family therapy, and specialized mental health support.',
  provider: {
    '@id': 'https://metrocounselling.ca/#organization'
  },
  availableService: [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalService',
      name: 'Individual Counselling',
      description: 'One-on-one counselling sessions for personal growth and mental health support.'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalService',
      name: 'Marriage Counselling',
      description: 'Professional marriage counselling and couples therapy services.'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalService',
      name: 'Family Therapy',
      description: 'Counselling services for families to improve communication and resolve conflicts.'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalService',
      name: 'Mental Health Counselling',
      description: 'Specialized therapy for anxiety, depression, and other mental health concerns.'
    }
  ]
};

const schemaConfig: Record<string, SchemaTypes | SchemaTypes[]> = {
  home: [baseSchema, serviceSchema],
  'individual-counselling-therapy-surrey': {
    '@context': 'https://schema.org',
    '@type': 'MedicalTherapy',
    name: 'Individual Counselling',
    description: 'Professional one-on-one counselling tailored to your needs in Surrey, BC.',
    provider: baseSchema,
    medicalSpecialty: ['Psychotherapy', 'CounsellingService'],
    relevantSpecialty: ['Mental Health', 'Personal Development']
  },
  'couples-therapy-marriage-counselling-surrey': {
    '@context': 'https://schema.org',
    '@type': 'MedicalTherapy',
    name: 'Marriage Counselling & Couples Therapy',
    description: 'Expert marriage counselling and couples therapy services in Surrey, BC.',
    provider: baseSchema,
    medicalSpecialty: ['CounsellingService', 'MarriageCounselling'],
    relevantSpecialty: ['Relationship Counselling', 'Marriage Therapy']
  }
};

export const getSchemaConfig = (page: string): SchemaTypes | SchemaTypes[] => {
  return schemaConfig[page] || baseSchema;
};

export default schemaConfig;
